import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Paper, Typography, Button, TextField, Select, MenuItem,
  FormControl, CircularProgress, Chip, Stack, Pagination,
  Table, TableBody, TableCell, TableHead, TableRow, Checkbox, ListItemText,
  Divider, useMediaQuery,
} from '@mui/material';
import {
  ArrowBack,
  Download as DownloadIcon,
  AttachMoney as PayIcon,
  ReplayOutlined as RefundIcon,
  BarChart as NetIcon,
  CreditCard as CardIcon,
  Receipt as CheckIcon,
} from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import { useSnackbar } from 'notistack';
import useAxiosPrivate from '../../../../Hooks/useAxiosPrivate';
import styles from './RevenueReport.module.css';

// ─── Helpers ─────────────────────────────────────────────────────────────────

const localDateStr = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const fmtAmt = (n) => {
  const abs = Math.abs(n);
  const str = abs.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return n < 0 ? `($${str})` : `$${str}`;
};

const fmtDate = (d) => {
  if (!d) return '—';
  const date = new Date(d);
  if (isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
};

// Refund metadata is sometimes stored as a JSON blob in the Notes column.
// Strip it down to just the human-readable note (or item descriptions if no note).
const cleanNotes = (value) => {
  if (!value) return '';
  if (typeof value !== 'string') return String(value);
  const trimmed = value.trim();
  if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) return value;
  try {
    const parsed = JSON.parse(trimmed);
    if (parsed && typeof parsed === 'object') {
      if (typeof parsed.notes === 'string' && parsed.notes.trim()) return parsed.notes.trim();
      if (Array.isArray(parsed.items) && parsed.items.length) {
        return parsed.items.map((i) => i?.description).filter(Boolean).join('; ');
      }
      return '';
    }
  } catch {
    // Not valid JSON — fall through and show as-is.
  }
  return value;
};

// Strip the "ORD-" prefix the API prepends to OrderIDs for display.
const stripOrderPrefix = (v) => (typeof v === 'string' ? v.replace(/^ORD-/, '') : v);

const PAYMENT_METHOD_CHIP = {
  'Credit Card':        { label: 'Credit Card',        sx: { bgcolor: '#1565c0', color: '#fff', fontWeight: 600 } },
  'Check':              { label: 'Check',               sx: { bgcolor: '#2e7d32', color: '#fff', fontWeight: 600 } },
  'Credit Card Refund': { label: 'Credit Card Refund',  sx: { bgcolor: '#c62828', color: '#fff', fontWeight: 600 } },
  'Check Refund':       { label: 'Check Refund',        sx: { bgcolor: '#e65100', color: '#fff', fontWeight: 600 } },
};

// Payment status: Paid / Refunded (pending orders are not payments, so they do
// not appear in the Revenue Report — see the Outstanding Payments Report).
const PAYMENT_STATUS_CHIP = {
  'Paid':     { sx: { bgcolor: '#e8f5e9', color: '#1b5e20', fontWeight: 600, border: '1px solid #a5d6a7' } },
  'Refunded': { sx: { bgcolor: '#ffebee', color: '#c62828', fontWeight: 600, border: '1px solid #ef9a9a' } },
  'Pending':  { sx: { bgcolor: '#fff8e1', color: '#e65100', fontWeight: 600, border: '1px solid #ffcc80' } },
};

// How the payment was initiated.
const PAYMENT_CHANNEL_CHIP = {
  'Online': { sx: { bgcolor: '#e3f2fd', color: '#1565c0', fontWeight: 600 } },
  'Phone':  { sx: { bgcolor: '#f3e5f5', color: '#6a1b9a', fontWeight: 600 } },
  'Check':  { sx: { bgcolor: '#fff3e0', color: '#e65100', fontWeight: 600 } },
};

// ─── Stat Card ────────────────────────────────────────────────────────────────

const StatCard = ({ icon, label, amount, count, amountColor = '#003063', iconBg = '#e8f0fe' }) => (
  <Paper elevation={0} className={styles.statCard}>
    <Box className={styles.statCardIcon} sx={{ bgcolor: iconBg }}>{icon}</Box>
    <Box>
      <Typography className={styles.statCardLabel}>{label}</Typography>
      <Typography className={styles.statCardAmount} sx={{ color: amountColor }}>
        {fmtAmt(amount)}
      </Typography>
      {count !== undefined && (
        <Typography className={styles.statCardCount}>{count} Transaction{count !== 1 ? 's' : ''}</Typography>
      )}
    </Box>
  </Paper>
);

// ─── Main Component ───────────────────────────────────────────────────────────

const RevenueReport = () => {
  const axiosPrivate = useAxiosPrivate();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const isMobile = useMediaQuery("(max-width:600px)");

  const now = new Date();
  const DEFAULT_FROM = localDateStr(new Date(now.getFullYear(), now.getMonth(), 1));
  const DEFAULT_TO   = localDateStr(now);

  const [dateFrom,            setDateFrom]            = useState(DEFAULT_FROM);
  const [dateTo,              setDateTo]              = useState(DEFAULT_TO);
  const [pmFilter,            setPmFilter]            = useState('all');
  const [ptFilter,            setPtFilter]            = useState('all');
  // Multi-select: empty array = "all"
  const [acctFilter,          setAcctFilter]          = useState([]);

  const [rows,                setRows]                = useState([]);
  const [summary,             setSummary]             = useState([]);
  const [isLoading,           setIsLoading]           = useState(false);
  const [hasRun,              setHasRun]              = useState(false);

  // Selected event objects (keep so chips render even if not in current search results)
  const [selectedEvents, setSelectedEvents] = useState([]);
  // Current page of results from server-side event search
  const [eventOptions, setEventOptions] = useState([]);
  const [eventSearch, setEventSearch] = useState('');
  const [debouncedEventSearch, setDebouncedEventSearch] = useState('');
  const [isLoadingEvents, setIsLoadingEvents] = useState(false);
  const [eventDropdownOpen, setEventDropdownOpen] = useState(false);

  const [accountCodes, setAccountCodes] = useState([]);

  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });
  const [sortModel, setSortModel] = useState([]);

  // Account codes come from the Settings project groups (Settings -> Categories
  // -> Partnership & Accounts Receivable), so the filter matches the report's
  // Account Code column, which shows those same accounting numbers.
  useEffect(() => {
    let cancelled = false;
    axiosPrivate.get('/api/events/projectgroups')
      .then(res => { if (!cancelled) setAccountCodes(res.data || []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [axiosPrivate]);

  // Debounce the event search input (300 ms)
  useEffect(() => {
    const t = setTimeout(() => setDebouncedEventSearch(eventSearch.trim()), 300);
    return () => clearTimeout(t);
  }, [eventSearch]);

  // Fetch event options (server-side search). Only when dropdown is open.
  useEffect(() => {
    if (!eventDropdownOpen) return;
    let cancelled = false;
    setIsLoadingEvents(true);
    axiosPrivate.get('/api/reports/events', {
      params: { search: debouncedEventSearch || undefined, limit: 50 },
    })
      .then(res => { if (!cancelled) setEventOptions(res.data || []); })
      .catch(() => { if (!cancelled) setEventOptions([]); })
      .finally(() => { if (!cancelled) setIsLoadingEvents(false); });
    return () => { cancelled = true; };
  }, [axiosPrivate, debouncedEventSearch, eventDropdownOpen]);

  const buildUrl = useCallback((fmt = 'json') => {
    const eventIds = selectedEvents.map((e) => e.EventsID).filter(Boolean);
    const p = new URLSearchParams({
      dateFrom,
      dateTo,
      paymentMethod: pmFilter,
      paymentType: ptFilter,
      // Multi-select: comma-separated when set, "all" when empty.
      accountCode: acctFilter.length ? acctFilter.join(',') : 'all',
      eventId:     eventIds.length   ? eventIds.join(',')   : 'all',
      format: fmt,
    });
    return `/api/reports/revenue?${p.toString()}`;
  }, [dateFrom, dateTo, pmFilter, ptFilter, acctFilter, selectedEvents]);

  const runReport = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await axiosPrivate.get(buildUrl('json'));
      setRows(res.data.rows || []);
      setSummary(res.data.summary || []);
      setHasRun(true);
      setPaginationModel(prev => ({ ...prev, page: 0 }));
      if ((res.data.rows || []).length === 0) {
        enqueueSnackbar('No transactions found for this date range.', { variant: 'info' });
      }
    } catch {
      enqueueSnackbar('Failed to load revenue report.', { variant: 'error' });
    } finally {
      setIsLoading(false);
    }
  }, [axiosPrivate, buildUrl, enqueueSnackbar]);

  // Auto-run on mount with current month defaults
  useEffect(() => { runReport(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDownload = async (fmt) => {
    try {
      const res = await axiosPrivate.get(buildUrl(fmt), { responseType: 'blob' });
      const url  = window.URL.createObjectURL(new Blob([res.data]));
      const a    = document.createElement('a');
      a.href     = url;
      a.download = `Revenue_Report_${dateFrom}_to_${dateTo}.${fmt === 'csv' ? 'csv' : 'xlsx'}`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      enqueueSnackbar('Export failed.', { variant: 'error' });
    }
  };

  // Stats computed from all rows (not just current page)
  const stats = useMemo(() => {
    const payments    = rows.filter(r => !r.isRefund);
    const refunds     = rows.filter(r => r.isRefund);
    const totalPay    = payments.reduce((s, r) => s + r.amountPaidRefunded, 0);
    const totalRef    = refunds.reduce((s, r) => s + Math.abs(r.amountPaidRefunded), 0);
    const ccPay       = payments.filter(r => r.paymentMethod === 'Credit Card').reduce((s, r) => s + r.amountPaidRefunded, 0);
    const chkPay      = payments.filter(r => r.paymentMethod === 'Check').reduce((s, r) => s + r.amountPaidRefunded, 0);
    return {
      totalPay,  payCount:  payments.length,
      totalRef,  refCount:  refunds.length,
      netTotal:  totalPay - totalRef,
      ccPay,     ccCount:   payments.filter(r => r.paymentMethod === 'Credit Card').length,
      chkPay,    chkCount:  payments.filter(r => r.paymentMethod === 'Check').length,
    };
  }, [rows]);

  // Summary totals row
  const summaryTotals = useMemo(() => summary.reduce(
    (t, r) => ({
      paymentsCC:    t.paymentsCC    + r.paymentsCC,
      paymentsCheck: t.paymentsCheck + r.paymentsCheck,
      refundsCC:     t.refundsCC     + r.refundsCC,
      refundsCheck:  t.refundsCheck  + r.refundsCheck,
      netTotal:      t.netTotal      + r.netTotal,
      count:         t.count         + r.count,
    }),
    { paymentsCC: 0, paymentsCheck: 0, refundsCC: 0, refundsCheck: 0, netTotal: 0, count: 0 }
  ), [summary]);

  // Pagination
  const totalCount = rows.length;
  const pageCount  = Math.ceil(totalCount / paginationModel.pageSize) || 1;
  const startIdx   = paginationModel.page * paginationModel.pageSize;
  // Sort the ENTIRE result set (not just the current page) before paginating.
  // Numeric-aware so amount columns sort correctly (not as strings).
  const sortedRows = (() => {
    if (!sortModel.length) return rows;
    const { field, sort } = sortModel[0];
    if (!field || !sort) return rows;
    const dir = sort === 'desc' ? -1 : 1;
    const num = (v) => {
      const n = Number(String(v).replace(/[$,\s]/g, ''));
      return Number.isFinite(n) && String(v ?? '').trim() !== '' ? n : null;
    };
    return [...rows].sort((a, b) => {
      const an = num(a?.[field]), bn = num(b?.[field]);
      if (an !== null && bn !== null) return (an - bn) * dir;
      const as = String(a?.[field] ?? '').toLowerCase();
      const bs = String(b?.[field] ?? '').toLowerCase();
      if (as < bs) return -1 * dir;
      if (as > bs) return 1 * dir;
      return 0;
    });
  })();
  const pageRows   = sortedRows.slice(startIdx, startIdx + paginationModel.pageSize);

  // DataGrid columns
  const columns = useMemo(() => [
    {
      field: 'orderId', headerName: 'Order ID', width: 108,
      renderCell: p => <Typography variant="body2" fontWeight={600}>{stripOrderPrefix(p.value)}</Typography>,
    },
    { field: 'transactionNum', headerName: 'Transaction #', width: 130 },
    {
      field: 'orderCreatedDate', headerName: 'Order Created', width: 120,
      renderCell: p => <Typography variant="body2">{fmtDate(p.value)}</Typography>,
    },
    {
      field: 'datePaidRefunded', headerName: 'Date Paid / Refunded', width: 140,
      renderCell: p => <Typography variant="body2">{fmtDate(p.value)}</Typography>,
    },
    { field: 'customerName', headerName: 'Customer Name', flex: 1, minWidth: 150 },
    { field: 'eventName',    headerName: 'Event Name',    flex: 1.4, minWidth: 170 },
    {
      field: 'orderTotal', headerName: 'Order Total', width: 108,
      renderCell: p => <Typography variant="body2">{fmtAmt(p.value)}</Typography>,
    },
    {
      field: 'amountPaidRefunded', headerName: 'Amount Paid / Refunded', width: 160,
      renderCell: p => (
        <Typography variant="body2" fontWeight={600} sx={{ color: p.value < 0 ? '#c62828' : '#1b5e20' }}>
          {fmtAmt(p.value)}
        </Typography>
      ),
    },
    {
      field: 'accountCode', headerName: 'Account Code', width: 130,
      renderCell: p => (
        <Box>
          <Typography variant="body2" fontWeight={600}>{p.value || '—'}</Typography>
          {p.row.accountDescription && (
            <Typography variant="caption" color="text.secondary">{p.row.accountDescription}</Typography>
          )}
        </Box>
      ),
    },
    {
      field: 'paymentMethod', headerName: 'Payment Method', width: 170,
      renderCell: p => {
        const cfg = PAYMENT_METHOD_CHIP[p.value];
        return cfg
          ? <Chip label={cfg.label} size="small" sx={{ ...cfg.sx, fontSize: 11 }} />
          : <Typography variant="body2">{p.value}</Typography>;
      },
    },
    {
      field: 'paymentChannel', headerName: 'Payment Channel', width: 140,
      renderCell: p => {
        const cfg = PAYMENT_CHANNEL_CHIP[p.value];
        return p.value
          ? <Chip label={p.value} size="small" sx={{ ...(cfg?.sx || {}), fontSize: 11 }} />
          : <Typography variant="body2">—</Typography>;
      },
    },
    {
      field: 'paymentStatus', headerName: 'Payment Status', width: 130,
      renderCell: p => {
        const cfg = PAYMENT_STATUS_CHIP[p.value];
        return p.value
          ? <Chip label={p.value} size="small" variant="outlined" sx={{ ...(cfg?.sx || {}), fontSize: 11 }} />
          : <Typography variant="body2">—</Typography>;
      },
    },
    {
      field: 'notes', headerName: 'Notes', flex: 1, minWidth: 140,
      renderCell: p => <Typography variant="body2">{cleanNotes(p.value) || '—'}</Typography>,
    },
  ], []);

  const headerBanner = (
    <div className="full-row light-blue">
      <div className="container-fluid">
        <div className="row text-white pt-3 pb-3">
          <div className="col-md-12">
            <h1 className="text-uppercase">Revenue Report</h1>
            <p>Monthly report of all payments and refunds for events, including credit cards and checks.</p>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <main>
      {headerBanner}

      <div className="full-row white">
        <Box className={styles.container}>

          {/* Back button */}
          <Box className={styles.backRow}>
            <Button variant="outlined" startIcon={<ArrowBack />} onClick={() => navigate('/admin/reporting')}>
              Back to Reports
            </Button>
          </Box>

          {/* Filter panel */}
          <Paper className={styles.filterPanel} elevation={0}>
            <Box className={styles.filtersGrid}>

              {/* Date range */}
              <Box className={styles.filterGroup}>
                <Typography className={styles.filterLabel}>Date Paid / Refunded</Typography>
                <Box className={styles.dateRange}>
                  <TextField type="date" size="small" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className={styles.dateField} inputProps={{ max: dateTo }} />
                  <Typography variant="body2" color="text.secondary" sx={{ mx: 0.5 }}>—</Typography>
                  <TextField type="date" size="small" value={dateTo} onChange={e => setDateTo(e.target.value)} className={styles.dateField} inputProps={{ min: dateFrom }} />
                </Box>
              </Box>

              {/* Payment Method */}
              <Box className={styles.filterGroup}>
                <Typography className={styles.filterLabel}>Payment Method</Typography>
                <FormControl size="small" className={styles.filterSelect}>
                  <Select value={pmFilter} onChange={e => setPmFilter(e.target.value)} displayEmpty>
                    <MenuItem value="all">All Payment Methods</MenuItem>
                    <MenuItem value="credit-card">Credit Card (Authorize.net)</MenuItem>
                    <MenuItem value="phone">Pay by Phone</MenuItem>
                    <MenuItem value="check">Pay by Check</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              {/* Payment Type */}
              <Box className={styles.filterGroup}>
                <Typography className={styles.filterLabel}>Payment Type</Typography>
                <FormControl size="small" className={styles.filterSelect}>
                  <Select value={ptFilter} onChange={e => setPtFilter(e.target.value)} displayEmpty>
                    <MenuItem value="all">All (Payments &amp; Refunds)</MenuItem>
                    <MenuItem value="payments">Payments Only</MenuItem>
                    <MenuItem value="refunds">Refunds Only</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              {/* Account Code (multi-select) */}
              <Box className={styles.filterGroup}>
                <Typography className={styles.filterLabel}>Account Code</Typography>
                <FormControl size="small" className={styles.filterSelect}>
                  <Select
                    multiple
                    value={acctFilter}
                    onChange={e => setAcctFilter(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
                    displayEmpty
                    renderValue={(selected) =>
                      !selected || selected.length === 0
                        ? 'All'
                        : selected.length === 1
                          ? selected[0]
                          : `${selected.length} selected`
                    }
                  >
                    {(() => {
                      // Match the report column: accounting number when set, else the name.
                      const withValue = accountCodes.map(a => ({
                        itemId:    a.ItemID,
                        codeValue: a.AccountingNumber || a.Name,
                        label:     a.AccountingNumber ? `${a.AccountingNumber} — ${a.Name}` : a.Name,
                      }));
                      const selected   = withValue.filter(a => acctFilter.indexOf(a.codeValue) > -1);
                      const unselected = withValue.filter(a => acctFilter.indexOf(a.codeValue) === -1);
                      const renderItem = (a, checked) => (
                        <MenuItem key={a.itemId} value={a.codeValue}>
                          <Checkbox checked={checked} />
                          <ListItemText primary={a.label} />
                        </MenuItem>
                      );
                      return [
                        // Selected group — pinned at the top so users see what's chosen.
                        selected.length > 0 && (
                          <Box
                            key="acct-sel-hdr"
                            sx={{ px: 2, py: 0.5, fontSize: 12, fontWeight: 700, color: 'text.secondary' }}
                          >
                            Selected ({selected.length})
                          </Box>
                        ),
                        ...selected.map(a => renderItem(a, true)),
                        selected.length > 0 && <Divider key="acct-sel-div" sx={{ my: 0.5 }} />,
                        ...unselected.map(a => renderItem(a, false)),
                      ];
                    })()}
                  </Select>
                </FormControl>
              </Box>

              {/* Event (multi-select; search box lives inside the dropdown) */}
              <Box className={`${styles.filterGroup} ${styles.eventFilterGroup}`}>
                <Typography className={styles.filterLabel}>Event</Typography>
                <FormControl size="small" className={styles.filterSelect}>
                  <Select
                    multiple
                    open={eventDropdownOpen}
                    onOpen={() => setEventDropdownOpen(true)}
                    onClose={() => { setEventDropdownOpen(false); setEventSearch(''); }}
                    displayEmpty
                    value={selectedEvents.map((e) => e.EventsID)}
                    onChange={() => {/* selection handled per-item to keep full event objects */}}
                    renderValue={() =>
                      selectedEvents.length === 0
                        ? 'All'
                        : selectedEvents.length === 1
                          ? selectedEvents[0].BP_Title
                          : `${selectedEvents.length} selected`
                    }
                    MenuProps={{
                      autoFocus: false,
                      disableAutoFocusItem: true,
                      PaperProps: { sx: { maxHeight: 420, minWidth: 480 } },
                    }}
                  >
                    {/* Sticky search field at the top of the dropdown */}
                    <Box
                      sx={{ p: 1, position: 'sticky', top: 0, bgcolor: '#fff', zIndex: 2, borderBottom: '1px solid #eee' }}
                      onClick={(e) => e.stopPropagation()}
                      onMouseDown={(e) => e.stopPropagation()}
                      onKeyDown={(e) => e.stopPropagation()}
                    >
                      <TextField
                        autoFocus
                        fullWidth
                        size="small"
                        placeholder="Search events…"
                        value={eventSearch}
                        onChange={(e) => setEventSearch(e.target.value)}
                        // Stop the Select from hijacking keystrokes (its built-in
                        // type-ahead-to-select moves focus and closes the menu).
                        onKeyDown={(e) => {
                          if (e.key !== 'Escape') e.stopPropagation();
                        }}
                        onClick={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                        InputProps={{
                          endAdornment: isLoadingEvents
                            ? <CircularProgress color="inherit" size={16} />
                            : null,
                        }}
                      />
                    </Box>

                    {/* ── Selected group: pinned at the top so users always see what's chosen ── */}
                    {selectedEvents.length > 0 && (
                      <Box
                        sx={{
                          px: 2, pt: 1, pb: 0.5, fontSize: 11, fontWeight: 700,
                          textTransform: 'uppercase', letterSpacing: 0.6, color: '#666',
                        }}
                      >
                        Selected ({selectedEvents.length})
                      </Box>
                    )}
                    {selectedEvents.map((s) => (
                      <MenuItem
                        key={`sel-${s.EventsID}`}
                        value={s.EventsID}
                        onClick={() => {
                          setSelectedEvents((prev) =>
                            prev.filter((p) => Number(p.EventsID) !== Number(s.EventsID))
                          );
                        }}
                        sx={{ whiteSpace: 'normal' }}
                      >
                        <Checkbox checked />
                        <ListItemText primary={s.BP_Title} />
                      </MenuItem>
                    ))}
                    {selectedEvents.length > 0 && <Divider sx={{ my: 0.5 }} />}

                    {/* Empty / loading states */}
                    {!isLoadingEvents && eventOptions.length === 0 && (
                      <Box sx={{ p: 2, color: 'text.secondary', fontSize: 14 }}>
                        {debouncedEventSearch ? 'No matching events' : 'Type to search…'}
                      </Box>
                    )}

                    {/* Result rows — skip any already shown in the Selected group */}
                    {eventOptions
                      .filter((ev) => !selectedEvents.some((s) => Number(s.EventsID) === Number(ev.EventsID)))
                      .map((ev) => (
                        <MenuItem
                          key={ev.EventsID}
                          value={ev.EventsID}
                          onClick={() => setSelectedEvents((prev) => [...prev, ev])}
                          sx={{ whiteSpace: 'normal' }}
                        >
                          <Checkbox checked={false} />
                          <ListItemText primary={ev.BP_Title} />
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              </Box>

            </Box>

            <Box className={styles.filterActions}>
              <Button
                variant="contained"
                onClick={runReport}
                disabled={isLoading}
                startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : null}
                className={styles.runBtn}
              >
                {isLoading ? 'Loading…' : 'Run Report'}
              </Button>
            </Box>
          </Paper>

          {/* Loading state */}
          {isLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress sx={{ color: '#003063' }} />
            </Box>
          )}

          {/* Results */}
          {!isLoading && hasRun && (
            <>
              {/* Stat cards */}
              <Box className={styles.statsRow}>
                <StatCard icon={<PayIcon sx={{ color: '#2e7d32' }} />} label="TOTAL PAYMENTS"   amount={stats.totalPay}  count={stats.payCount}  amountColor="#1b5e20" iconBg="#e8f5e9" />
                <StatCard icon={<RefundIcon sx={{ color: '#c62828' }} />} label="TOTAL REFUNDS" amount={-stats.totalRef} count={stats.refCount}  amountColor="#c62828" iconBg="#ffebee" />
                <StatCard icon={<NetIcon sx={{ color: '#1565c0' }} />}    label="NET TOTAL"     amount={stats.netTotal}                           amountColor={stats.netTotal >= 0 ? '#003063' : '#c62828'} iconBg="#e8f0fe" />
                <StatCard icon={<CardIcon sx={{ color: '#1565c0' }} />}   label="CREDIT CARD (AUTHORIZE.NET)" amount={stats.ccPay}  count={stats.ccCount}  amountColor="#1565c0" iconBg="#e3f2fd" />
                <StatCard icon={<CheckIcon sx={{ color: '#e65100' }} />}  label="CHECK PAYMENTS" amount={stats.chkPay} count={stats.chkCount} amountColor="#e65100" iconBg="#fff3e0" />
              </Box>

              {/* Detail Report header */}
              <Box className={styles.sectionHeader}>
                <Typography className={styles.sectionTitle}>DETAIL REPORT</Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button variant="outlined" size="small" startIcon={<DownloadIcon />} onClick={() => handleDownload('csv')}   disabled={rows.length === 0} className={styles.exportBtn}>Export CSV</Button>
                  <Button variant="outlined" size="small" startIcon={<DownloadIcon />} onClick={() => handleDownload('excel')} disabled={rows.length === 0} className={styles.exportBtn}>Export Excel</Button>
                </Box>
              </Box>

              {rows.length === 0 ? (
                <Paper elevation={0} sx={{ p: 4, textAlign: 'center', border: '1px solid #e0e0e0', borderRadius: 2 }}>
                  <Typography color="text.secondary">No transactions found for the selected filters.</Typography>
                </Paper>
              ) : (
                <>
                  {/* DataGrid — styled to match the Finances/Users grid across the app */}
                  <Paper className={styles.dataGridPaper} elevation={0}>
                    <DataGrid
                      rows={pageRows}
                      columns={columns}
                      getRowId={row => row.rowId}
                      autoHeight
                      getRowHeight={() => 'auto'}
                      hideFooterPagination
                      sortingMode="server"
                      sortModel={sortModel}
                      onSortModelChange={(m) => {
                        setSortModel(m);
                        setPaginationModel((prev) => ({ ...prev, page: 0 }));
                      }}
                      disableRowSelectionOnClick
                      disableColumnFilter
                      disableDensitySelector
                      className="usersDataGrid"
                    />
                  </Paper>

                  {/* Pagination — matches Finances/Users layout */}
                  <Paper className={styles.paginationPaper} elevation={0}>
                    <Stack
                      direction={{ xs: 'column', md: 'row' }}
                      justifyContent="space-between"
                      alignItems="center"
                      spacing={2}
                      sx={{ width: '100%', flexWrap: 'wrap' }}
                    >
                      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                        Showing {totalCount > 0 ? startIdx + 1 : 0} to {Math.min(startIdx + paginationModel.pageSize, totalCount)} of {totalCount} rows
                      </Typography>

                      <Stack direction="row" alignItems="center" sx={{ gap: 2, order: { xs: -1, md: 0 }, '& .MuiPagination-ul': { flexWrap: 'wrap', justifyContent: 'center', rowGap: 1 } }}>
                        <Pagination
                          count={pageCount}
                          page={paginationModel.page + 1}
                          onChange={(_, p) => setPaginationModel(prev => ({ ...prev, page: p - 1 }))}
                          color="primary" variant="outlined" shape="rounded" showFirstButton showLastButton
                          siblingCount={isMobile ? 0 : 1}
                          boundaryCount={1}
                          className="usersPagination"
                        />
                      </Stack>

                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, whiteSpace: 'nowrap' }}>
                        <Typography variant="body2" color="text.secondary">Show:</Typography>
                        <TextField
                          select size="small" value={paginationModel.pageSize}
                          onChange={e => setPaginationModel({ page: 0, pageSize: parseInt(e.target.value) })}
                          SelectProps={{ native: true }}
                          sx={{ minWidth: 80 }}
                          className="pageSizeField"
                        >
                          {[10, 25, 50, 100].map(v => <option key={v} value={v}>{v}</option>)}
                        </TextField>
                        <Typography variant="body2" color="text.secondary">per page</Typography>
                      </Box>
                    </Stack>
                  </Paper>
                </>
              )}

              {/* Summary Report */}
              {summary.length > 0 && (
                <>
                  <Typography className={styles.sectionTitle} sx={{ mt: 4 }}>
                    SUMMARY REPORT (GROUPED BY ACCOUNT CODE)
                  </Typography>
                  <Paper className={styles.summaryPaper} elevation={0}>
                    <Table size="small" className={styles.summaryTable}>
                      <TableHead>
                        <TableRow className={styles.summaryHead}>
                          <TableCell>Account Code</TableCell>
                          <TableCell>Account Description</TableCell>
                          <TableCell align="right">Payments (Credit Card)</TableCell>
                          <TableCell align="right">Payments (Check)</TableCell>
                          <TableCell align="right">Refunds (Credit Card)</TableCell>
                          <TableCell align="right">Refunds (Check)</TableCell>
                          <TableCell align="right">Net Total</TableCell>
                          <TableCell align="right"># of Transactions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {summary.map(row => (
                          <TableRow key={row.accountCode} className={styles.summaryRow}>
                            <TableCell><Typography variant="body2" fontWeight={600}>{row.accountCode}</Typography></TableCell>
                            <TableCell><Typography variant="body2">{row.accountDescription}</Typography></TableCell>
                            <TableCell align="right"><Typography variant="body2">{fmtAmt(row.paymentsCC)}</Typography></TableCell>
                            <TableCell align="right"><Typography variant="body2">{fmtAmt(row.paymentsCheck)}</Typography></TableCell>
                            <TableCell align="right"><Typography variant="body2" sx={{ color: row.refundsCC < 0 ? '#c62828' : 'inherit' }}>{fmtAmt(row.refundsCC)}</Typography></TableCell>
                            <TableCell align="right"><Typography variant="body2" sx={{ color: row.refundsCheck < 0 ? '#c62828' : 'inherit' }}>{fmtAmt(row.refundsCheck)}</Typography></TableCell>
                            <TableCell align="right"><Typography variant="body2" fontWeight={600} sx={{ color: row.netTotal >= 0 ? '#003063' : '#c62828' }}>{fmtAmt(row.netTotal)}</Typography></TableCell>
                            <TableCell align="right"><Typography variant="body2">{row.count}</Typography></TableCell>
                          </TableRow>
                        ))}

                        {/* Totals row */}
                        <TableRow className={styles.summaryTotalsRow}>
                          <TableCell colSpan={2}><Typography variant="body2" fontWeight={700} sx={{ color: '#003063' }}>TOTAL</Typography></TableCell>
                          <TableCell align="right"><Typography variant="body2" fontWeight={700} sx={{ color: '#003063' }}>{fmtAmt(summaryTotals.paymentsCC)}</Typography></TableCell>
                          <TableCell align="right"><Typography variant="body2" fontWeight={700} sx={{ color: '#003063' }}>{fmtAmt(summaryTotals.paymentsCheck)}</Typography></TableCell>
                          <TableCell align="right"><Typography variant="body2" fontWeight={700} sx={{ color: summaryTotals.refundsCC < 0 ? '#c62828' : '#003063' }}>{fmtAmt(summaryTotals.refundsCC)}</Typography></TableCell>
                          <TableCell align="right"><Typography variant="body2" fontWeight={700} sx={{ color: summaryTotals.refundsCheck < 0 ? '#c62828' : '#003063' }}>{fmtAmt(summaryTotals.refundsCheck)}</Typography></TableCell>
                          <TableCell align="right"><Typography variant="body2" fontWeight={700} sx={{ color: summaryTotals.netTotal >= 0 ? '#003063' : '#c62828' }}>{fmtAmt(summaryTotals.netTotal)}</Typography></TableCell>
                          <TableCell align="right"><Typography variant="body2" fontWeight={700} sx={{ color: '#003063' }}>{summaryTotals.count}</Typography></TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5, px: 1 }}>
                      All amounts are in USD. Refunds are shown as negative values.
                    </Typography>
                  </Paper>
                </>
              )}
            </>
          )}

        </Box>
      </div>
    </main>
  );
};

export default RevenueReport;
